/**
 * A collection of useful arbitraries that are used across model types.
 */

import {Decimal} from 'decimal.js';
import FastCheck from 'fast-check';

// Keep in sync with benchling/models/mixins/api_identified.py
const API_ID_LENGTH = 8;
const API_ID_SEPERATOR = '_';

// TODO(BNCH-57526): Replace this with a single Decimal.clone that we can use everywhere
// Upper decimal digit precision bound for floating point values.
const MAX_FLOAT_PRECISION = 15;
const BenchlingFloatPrecisionDecimal = Decimal.clone({precision: MAX_FLOAT_PRECISION});

/**
 * A FastCheck Arbitrary for floats which caps the precision at 15 digits of precision instead of JavaScript's
 * full 17 digits. This matches the expectations for the backend.
 *
 * @return a float number
 */
export function float(): FastCheck.Arbitrary<number> {
  // We need to apply an identity function to the Decimal to have the precision apply
  return FastCheck.float().map((num) => new BenchlingFloatPrecisionDecimal(num).add(0).toNumber());
}

/**
 * A FastCheck Arbitrary for API identifiers.  Just pass in the prefix and this will
 * kick out a legal API identifier for that prefix.
 *
 * @param prefixes - a vararg of prefixes to choose from when generating the API id
 * @return a legal API identifier with the given prefix
 */
export function apiIdentifierArbitrary(...prefixes: Array<string>): FastCheck.Arbitrary<string> {
  return FastCheck.tuple(
    FastCheck.constantFrom(...prefixes),
    FastCheck.hexaString(API_ID_LENGTH, API_ID_LENGTH)
  ).map(([prefix, id]) => prefix + API_ID_SEPERATOR + id);
}

/**
 * An arbitrary that produces strings usable for item names. Mainly, this ensures we don't
 * have leading or trailing spaces, or an empty string
 */
export function itemNameArbitrary(): FastCheck.Arbitrary<string> {
  return FastCheck.asciiString(3, 20)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/**
 * An arbitrary that produces an ISO timestamp string.
 */
export const modifiedAtArbitrary: FastCheck.Arbitrary<string> = FastCheck.date().map((date) =>
  date.toISOString()
);

/**
 * An arbitrary for a basic GraphQL UserNode shape used by many query fixtures.
 */
export const userNodeArbitrary: FastCheck.Arbitrary<{
  __typename: 'UserNode';
  id: string;
  name: string;
}> = FastCheck.record({
  __typename: FastCheck.constant('UserNode' as const),
  id: apiIdentifierArbitrary('ent'),
  name: itemNameArbitrary(),
});

/**
 * A FastCheck arbitrary combinator that closes over some state to provide a uniqueness guarantee.
 * This is useful for cases like when we want to make sure that an array of SchemaFields do not
 * have the same id repeated at all.
 *
 * Be aware that this may increase the amount of time required to generate a valid arbitrary as for every
 * collision that happens, this function will need to generate another value.
 *
 * @param arbitrary - the underlying Arbitrary that we want to use
 * @param hashFn - a function which extracts some key that we can check for collisions
 */
export function uniqueArbitrary<T1, T2>(
  arbitrary: FastCheck.Arbitrary<T1>,
  hashFn: (arbitrary: T1) => T2
): FastCheck.Arbitrary<T1> {
  const alreadyGenerated: Set<T2> = new Set();
  return arbitrary.filter((t) => {
    const hashValue = hashFn(t);
    if (!alreadyGenerated.has(hashValue)) {
      alreadyGenerated.add(hashValue);
      return true;
    }
    return false;
  });
}
